import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Upload,
  Download,
  Trash2,
  X,
  Briefcase,
  Plus,
  Search,
} from "lucide-react";
import api from "../api/api";
import Toast, { useToast } from "../components/Toast";
import ConfirmDialog, { useConfirm } from "./ConfirmDialog";
import {
  getLeadWorkspaceDetails,
  addLeadNoteEntry,
  addLeadActivityLog,
  uploadLeadAttachmentFile,
  updateLeadWorkspaceDetails,
  markLeadAsClosedStatus,
  deleteLeadWorkspaceRecord
} from "../api/auth";

// Converts an incoming date value (ISO string, or the RFC1123-style
// "Thu, 02 Jul 2026 00:00:00 GMT" string Flask's default JSON encoder
// produces for date/datetime objects) into a YYYY-MM-DD value suitable
// for a native <input type="date">.
function toDateInputValue(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

// Flask's default HTTP-date encoder always appends "GMT" to datetime values,
// even though the backend actually writes local server time (not real UTC).
// Parsing that string normally makes the browser treat it as true UTC and
// convert it again to local time, which double-shifts it (e.g. rolling the
// date over to the next day). Stripping the "GMT" label makes JS parse the
// string as local wall-clock time as-is, with no extra conversion.
function formatServerTimestamp(value) {
  if (!value) return "-";
  const stripped = String(value).replace(/\s*GMT$/i, "");
  const parsed = new Date(stripped);
  if (isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
}

// Formats a numeric amount using the Indian digit-grouping system (e.g. 1234567 -> "12,34,567")
function formatIndianCurrency(value) {
  if (value === null || value === undefined) return "";
  const sanitized = String(value).replace(/[^0-9.]/g, "");
  if (sanitized === "") return "";

  const [integerPartRaw, ...decimalParts] = sanitized.split(".");
  const decimalPart = decimalParts.length ? decimalParts.join("") : undefined;
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "") || "0";

  let formattedInteger;
  if (integerPart.length <= 3) {
    formattedInteger = integerPart;
  } else {
    const lastThree = integerPart.slice(-3);
    const remaining = integerPart.slice(0, -3);
    formattedInteger = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart !== undefined ? `${formattedInteger}` : formattedInteger;
}

// Strips comma separators back out, returning the raw numeric string for state/submission
function unformatCurrency(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/,/g, "");
}

export default function LeadDetail() {
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic States from Code Table
  const [pipelineStages, setPipelineStages] = useState([]);
  const [leadData, setLeadData] = useState(null);
  const [pocList, setPocList] = useState([]);
  
  const [isEditable, setIsEditable] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [files, setFiles] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState("");
  const [auditMeta, setAuditMeta] = useState({ updated_by_name: "Admin", owner_name: "Rinku" });
  const [levelOptions, setLevelOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [presalesOptions, setPresalesOptions] = useState([]);
  const [bidManagerOptions, setBidManagerOptions] = useState([]);
  const [relatedOpf, setRelatedOpf] = useState(null);
  const [revertingOpf, setRevertingOpf] = useState(false);

  // Quick-assign modals for Pre-Sales / Bid Manager (opened from the buttons
  // next to "Edit Workspace", independent of the main edit-workspace form)
  const [presalesModalOpen, setPresalesModalOpen] = useState(false);
  const [bidManagerModalOpen, setBidManagerModalOpen] = useState(false);
  const [assigningPresales, setAssigningPresales] = useState(false);
  const [assigningBidManager, setAssigningBidManager] = useState(false);

  // Admin detection mirrors the same localStorage-based check used on the
  // Leads and Deals list pages (user.department set at login).
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = (currentUser.department || "").toLowerCase() === "admin";
  
  const [openAddDeal, setOpenAddDeal] = useState(false);

  useEffect(() => {
    if (leadId) {
      fetchLeadWorkspaceData();
    }
    fetchDropdownOptions();
  }, [leadId]);

  const fetchDropdownOptions = async () => {
    try {
      const response = await api.get("/leads/meta-options");
      if (response.data?.success) {
        const meta = response.data.data || {};
        setLevelOptions(meta.levels || []);
        setStatusOptions(meta.statuses || []);
        setPresalesOptions(meta.presalesUsers || []);
        setBidManagerOptions(meta.bidManagerUsers || []);
      }
    } catch (err) {
      console.error("Error fetching level/status options:", err);
    }
  };

  const fetchLeadWorkspaceData = async () => {
    try {
      setLoading(true);
      const response = await getLeadWorkspaceDetails(leadId);
      
      if (response.data?.success) {
        const payload = response.data.data;
        const base = payload.base || {};
        
        // Use pipeline stages or static fallback to completely eliminate empty white space
        const fetchedStages = payload.stages && payload.stages.length > 0 
          ? payload.stages 
          : [
              { code_id: 101, code_short_description: "Drop" },
              { code_id: 102, code_short_description: "OnGoing" },
              { code_id: 103, code_short_description: "Lost" },
              { code_id: 104, code_short_description: "Won" },
            ];

        setPipelineStages(fetchedStages);
        setAuditMeta(payload.audit || { updated_by_name: "Admin", owner_name: "Rinku" });

        const normalizedLead = {
          ld_id: base.ld_id,
          customerId: base.ld_customer_id,
          leadId: base.ld_id,
          customerName: base.cust_name || "-",
          source: base.source || "-",
          // Prefer this opportunity's own saved copy; fall back to the
          // customer's master record only for leads created/saved before
          // these per-opportunity columns existed.
          address: base.address || base.cust_address || "-",
          city: base.city || base.cust_city || "-",
          state: base.state || base.cust_state || "-",
          pincode: base.pincode || base.cust_pincode || "-",
          cin: base.cin || base.cust_cin || "-",
          gst: base.gst || base.cust_gst || "-",
          // priority: base.priority || "-",
          level: base.level || "-",       
          status: base.status || "New",   
          presales: base.presales_name || "-",
          presalesId: base.ld_presales_id || "",
          bidManager: base.bid_manager_name || "-",
          bidManagerId: base.ld_bid_manager_id || "",
          statusRemark: base.ld_remarks || "",
          estimatedBudget: base.ld_estimated_budget ?? "",
          oem: base.ld_oem || "",
          oemAcMgr: base.ld_oem_ac_mgr || "",
          product: base.ld_product || "",
          expectedClosure: toDateInputValue(base.ld_expected_closure),
          drNumber: base.ld_dr_number || "",
          drExpiry: toDateInputValue(base.ld_dr_expiry),
          createdAt: formatServerTimestamp(base.ld_last_updated),
          owner: payload.audit?.owner_name || "Rinku",
        };

        setLeadData(normalizedLead);
        // Save Lead Matrix ID to localStorage
// Save Lead Matrix ID and Customer Name to localStorage
if (base.ld_id) {
  localStorage.setItem("lead_id", base.ld_id);
}

if (base.cust_name) {
  localStorage.setItem("customer_name", base.cust_name);
}

if (normalizedLead.status) {
  localStorage.setItem("lead_status", normalizedLead.status);
}
        setPocList(payload.poc || []);
        setNotes(payload.notes || []);
        setFiles(payload.attachments || []);
        setActivities(payload.activities || []);
        setRelatedOpf(payload.opf || null);
        setError(null);
      } else {
        setError("Failed to resolve lead elements.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An exception occurred while executing requests.");
    } finally {
      setLoading(false);
    }
  };

  const updateLeadField = (key, value) => {
    setLeadData((prev) => ({ ...prev, [key]: value }));
  };

  const isLeadClosed = leadData?.status?.toLowerCase() === "won";
  const isLeadOngoing = leadData?.status?.toLowerCase() === "ongoing";
  const isRaiseOpfEnabled = isLeadClosed || isLeadOngoing;
  const opfStatus = (relatedOpf?.status || "").toLowerCase();
  const hasDraftOpf = opfStatus === "draft";
  const hasSubmittedOpf = opfStatus === "submitted";
  // Won only locks status transitions once an OPF actually exists for this
  // lead (draft or submitted) - a Won lead with no OPF on file yet can still
  // have its status changed. Mirrors the same check the backend enforces.
  const hasAnyOpf = !!relatedOpf;
  const isStatusChangeLocked = isLeadClosed && hasAnyOpf;

  const handleStageClick = async (stageName) => {
    if (stageName.toLowerCase() === "won") {
      await handleMarkAsClosed();
      return;
    }
    
    if (isStatusChangeLocked) {
      showToast("This Opportunity is marked as Won and has an OPF on file. Status transitions are locked.", "warning");
      return;
    }

    try {
      const response = await api.patch(`/leads/${leadId}/stage`, { stage_name: stageName });
      if (response.data?.success) {
        fetchLeadWorkspaceData();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error changing workflow status stage.", "error");
    }
  };

  const handleMarkAsClosed = async () => {
    try {
      const response = await markLeadAsClosedStatus(leadId);
      if (response.data?.success) {
        showToast("Lead status successfully updated to Won.", "success");
        setIsEditable(false); 
        fetchLeadWorkspaceData();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error processing status transition.", "error");
    }
  };

  const handleSaveWorkspaceUpdates = async () => {
    if (!(await confirm("Save workflow modifications?"))) return;
    try {
      const updatePayload = {
        statusRemark: leadData.statusRemark,
        estimatedBudget: leadData.estimatedBudget,
        customerId: leadData.customerId,
        customerName: leadData.customerName,
        address: leadData.address,
        city: leadData.city,
        state: leadData.state,
        pincode: leadData.pincode,
        cin: leadData.cin,
        gst: leadData.gst,
        oem: leadData.oem,
        oemAcMgr: leadData.oemAcMgr,
        product: leadData.product,
        expectedClosure: leadData.expectedClosure,
        drNumber: leadData.drNumber,
        drExpiry: leadData.drExpiry,
        level: leadData.level,
        status: leadData.status,
        poc: pocList
      };

      const response = await updateLeadWorkspaceDetails(leadId, updatePayload);
      if (response.data?.success) {
        setIsEditable(false);
        fetchLeadWorkspaceData();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error updating entries rows.", "error");
    }
  };

  const handleDeleteLeadWorkspace = async () => {
    if (isLeadClosed) {
      showToast("Action Denied: Closed leads cannot be removed from active logs.", "warning");
      return;
    }
    if (!(await confirm("CRITICAL WARNING: Are you sure you want to permanently delete this Lead and all its history? This action cannot be undone."))) return;
    try {
      const response = await deleteLeadWorkspaceRecord(leadId);
      if (response.data?.success) {
        showToast("Lead purged successfully.", "success");
        setTimeout(() => navigate("/leads"), 1200);
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error deleting record context data parameters.", "error");
    }
  };

  const handleAssignPresales = async (presalesId) => {
    if (!presalesId) return;
    setAssigningPresales(true);
    try {
      const response = await api.patch(`/leads/${leadId}/presales`, { presalesId });
      if (response.data?.success) {
        showToast("Pre-Sales person assigned successfully.", "success");
        setPresalesModalOpen(false);
        fetchLeadWorkspaceData();
      } else {
        showToast(response.data?.message || "Error assigning Pre-Sales person.", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error assigning Pre-Sales person.", "error");
    } finally {
      setAssigningPresales(false);
    }
  };

  const handleAssignBidManager = async (bidManagerId) => {
    if (!bidManagerId) return;
    setAssigningBidManager(true);
    try {
      const response = await api.patch(`/leads/${leadId}/bid-manager`, { bidManagerId });
      if (response.data?.success) {
        showToast("Bid Manager assigned successfully.", "success");
        setBidManagerModalOpen(false);
        fetchLeadWorkspaceData();
      } else {
        showToast(response.data?.message || "Error assigning Bid Manager.", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error assigning Bid Manager.", "error");
    } finally {
      setAssigningBidManager(false);
    }
  };

  const addNewPocRowItem = () => {
    const freshPoc = {
      poc_id: null,
      poc_name: "",
      poc_email: "",
      poc_contact: "",
      poc_designation: ""
    };
    setPocList([...pocList, freshPoc]);
  };

  const updatePocRowField = (index, key, val) => {
    const updated = [...pocList];
    updated[index][key] = val;
    setPocList(updated);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await addLeadNoteEntry(leadId, { note: newNote });
      if (response.data?.success) {
        setNewNote("");
        fetchLeadWorkspaceData(); 
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error logging note.", "error");
    }
  };

  const [uploadingFile, setUploadingFile] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const uploadFile = async (event) => {
    let fileTarget = event.target.files?.[0];
    if (!fileTarget) return;
    const formData = new FormData();
    formData.append("file", fileTarget);
    setUploadingFile(true);
    try {
      const response = await uploadLeadAttachmentFile(leadId, formData);
      if (response.data?.success) {
        fetchLeadWorkspaceData();
      } else {
        showToast(response.data?.message || "File upload execution fault.", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "File upload execution fault.", "error");
    } finally {
      setUploadingFile(false);
      event.target.value = "";
    }
  };

  // Attachments only store a relative path (e.g. "/uploads/attachments/...")
  // and the download route requires the JWT auth header, so a plain <a href>
  // can't be used — it resolves against the wrong origin and carries no auth.
  // Fetch the file as a blob through the authenticated api client instead,
  // then trigger the browser's save dialog ourselves.
  const downloadAttachment = async (attachmentId, displayName) => {
    setDownloadingId(attachmentId);
    try {
      const response = await api.get(
        `/leads/${leadId}/attachments/${attachmentId}/download`,
        { responseType: "blob" }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = displayName || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast(err.response?.data?.message || "Error downloading attachment.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!(await confirm("Remove this attachment?"))) return;
    try {
      const response = await api.delete(`/leads/${leadId}/attachments/${attachmentId}`);
      if (response.data?.success) {
        fetchLeadWorkspaceData();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error removing attachment.", "error");
    }
  };

  const handleMakeOpfEditable = async () => {
    if (!(await confirm("Make this OPF editable? Its status will move back to Draft."))) return;
    setRevertingOpf(true);
    try {
      const response = await api.patch(`/leads/${leadId}/opf/make-editable`);
      if (response.data?.success) {
        fetchLeadWorkspaceData();
      } else {
        showToast(response.data?.message || "Error making OPF editable.", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error making OPF editable.", "error");
    } finally {
      setRevertingOpf(false);
    }
  };

  const addActivity = async () => {
    if (!newActivity.trim()) return;
    try {
      const response = await addLeadActivityLog(leadId, { summary: newActivity });
      if (response.data?.success) {
        setNewActivity("");
        fetchLeadWorkspaceData(); 
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error inserting activity trace.", "error");
    }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center font-medium bg-[#F1F5F9]">Loading workspace structures...</div>;
  if (error) return <div className="flex min-h-screen items-center justify-center font-medium text-red-500 bg-[#F1F5F9]">Error: {error}</div>;

  return (
    <div className="ml-[60px] min-h-screen bg-[#F1F5F9] p-3 sm:p-6 pt-16  mt-12">
      <Toast toast={toast} onClose={hideToast} />
      <ConfirmDialog confirmState={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />

      {/* Clickable Pipeline Track Selector Component Layout */}
      <div className="mb-5 rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {pipelineStages.map((stage) => {
            const isMatch = String(leadData?.status).toLowerCase() === String(stage.code_short_description).toLowerCase();
            return (
              <button
                key={stage.code_id}
                type="button"
                onClick={() => handleStageClick(stage.code_short_description)}
                className={`rounded-md px-4 py-2 text-sm font-medium transition cursor-pointer shadow-sm ${
                  isMatch 
                    ? "bg-blue-600 text-white shadow-sm" 
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 active:scale-95"
                }`}
              >
                {stage.code_short_description}
              </button>
            );
          })}
          
          {!isLeadClosed && (
            <button
              onClick={handleMarkAsClosed}
              className="ml-auto rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition shadow-sm"
            >
              Mark Status as Completed
            </button>
          )}
        </div>
      </div>

      <div className="mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border bg-white p-4 sm:p-5 shadow-sm">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{leadData.customerName}</h1>
          <p className="text-sm text-gray-500">Opportunity : {leadData.leadId} • {leadData.source} • Status: <span className="font-semibold text-blue-600 capitalize">{leadData.status}</span></p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setPresalesModalOpen(true)}
            className="rounded-md border px-4 py-2 text-sm font-medium shadow-sm bg-white text-slate-700 hover:bg-gray-50"
            title={leadData.presales !== "-" ? `Current: ${leadData.presales}` : undefined}
          >
            {leadData.presales !== "-" ? `PreSales: ${leadData.presales}` : "Choose Presales"}
          </button>

          <button
            type="button"
            onClick={() => setBidManagerModalOpen(true)}
            className="rounded-md border px-4 py-2 text-sm font-medium shadow-sm bg-white text-slate-700 hover:bg-gray-50"
            title={leadData.bidManager !== "-" ? `Current: ${leadData.bidManager}` : undefined}
          >
            {leadData.bidManager !== "-" ? `Bid Manager: ${leadData.bidManager}` : "Choose Bid Manager"}
          </button>

          {!isEditable ? (
            <button 
              type="button" 
              onClick={() => setIsEditable(true)} 
              disabled={isLeadClosed}
              className={`rounded-md border px-4 py-2 text-sm font-medium shadow-sm ${
                isLeadClosed 
                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed" 
                  : "bg-white text-slate-700 hover:bg-gray-50"
              }`}
            >
              Edit Workspace
            </button>
          ) : (
            <button 
              type="button" 
              onClick={handleSaveWorkspaceUpdates} 
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition shadow-sm"
            >
              Save Changes
            </button>
          )}
<button
  type="button"
  onClick={() =>
    navigate(`/leads/${leadId}/bid-information`, {
      state: {
        leadId: leadData?.leadId,
        customerName: leadData?.customerName,
        accountManager: leadData?.owner,
        projectValue: leadData?.estimatedBudget,
      },
    })
  }
  className="rounded-md border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
>
  Bid Sheet
</button>
          {hasSubmittedOpf ? (
            <button
              type="button"
              disabled
              className="rounded-md border px-4 py-2 text-sm font-medium shadow-sm cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
            >
              OPF Raised
            </button>
          ) : hasDraftOpf ? (
            <button
              type="button"
              onClick={() =>
                navigate(`/leads/${leadId}/raise-opf`, {
                  state: {
                    customerName: leadData?.customerName,
                    address: leadData?.address,
                    leadId: leadData?.leadId,
                    opfId: relatedOpf?.opf_id,
                    leadStatus: leadData?.status,
                  },
                })
              }
              className="rounded-md border px-4 py-2 text-sm font-medium transition shadow-sm border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer"
            >
              Edit OPF Details
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                navigate(`/leads/${leadId}/raise-opf`, {
                  state: {
                    customerName: leadData?.customerName,
                    address: leadData?.address,
                    leadId: leadData?.leadId,
                    leadStatus: leadData?.status,
                  },
                })
              }
              disabled={!isRaiseOpfEnabled}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition shadow-sm ${
                isRaiseOpfEnabled
                  ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer"
                  : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
              }`}
            >
              Raise OPF
            </button>
          )}

          <button
            type="button"
            onClick={handleDeleteLeadWorkspace}
            disabled={isLeadClosed}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white transition shadow-sm ${
              isLeadClosed 
                ? "bg-slate-300 cursor-not-allowed" 
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            <Trash2 size={16} className="inline mr-1" /> Delete Opportunity
          </button>
        </div>
      </div>

      {hasSubmittedOpf && isAdmin && (
        <div className="mb-5 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-5 py-3 shadow-sm">
          <span className="text-sm font-medium text-amber-800">Make the OPF Editable</span>
          <button
            type="button"
            onClick={handleMakeOpfEditable}
            disabled={revertingOpf}
            className="rounded-md border border-amber-500 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition disabled:cursor-wait disabled:opacity-50"
          >
            {revertingOpf ? "Updating..." : "Make Editable"}
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[68%_32%]">
        <div>
          <RoleCard title="Opportunity Details" icon={<Briefcase size={18} />} accentColor="border-t-blue-600">
            <div className="space-y-8">
              <div>
                
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  <EditableField label="Opportunity ID" value={leadData.leadId} editable={false} monospace />
                  <EditableField label="Customer Company Name" value={leadData.customerName} editable={isEditable} onChange={(value) => updateLeadField("customerName", value)} />
                  <EditableField label="Source" value={leadData.source} editable={false} />
                  <EditableField label="Registered Address" value={leadData.address} editable={isEditable} onChange={(value) => updateLeadField("address", value)} className="md:col-span-2 lg:col-span-3" />
                  <EditableField label="City" value={leadData.city} editable={isEditable} onChange={(value) => updateLeadField("city", value)} />
                  <EditableField label="State" value={leadData.state} editable={isEditable} onChange={(value) => updateLeadField("state", value)} />
                  <EditableField label="Pincode" value={leadData.pincode} editable={isEditable} onChange={(value) => updateLeadField("pincode", value)} />
                  <EditableField label="Corporate Identification Number (CIN)" value={leadData.cin} editable={isEditable} onChange={(value) => updateLeadField("cin", value)} monospace />
                  <EditableField label="GST" value={leadData.gst} editable={isEditable} onChange={(value) => updateLeadField("gst", value)} monospace />
                  <EditableField label="Amount" value={leadData.estimatedBudget} editable={isEditable} onChange={(value) => updateLeadField("estimatedBudget", value)} format={formatIndianCurrency} parse={unformatCurrency} prefix="₹" />
                  {/* <EditableField label="Priority" value={leadData.priority} editable={false} /> */}
                  <SelectField label="Stages" value={leadData.level} editable={isEditable} onChange={(value) => updateLeadField("level", value)} options={levelOptions} />
                  <SelectField label="Status" value={leadData.status} editable={isEditable} onChange={(value) => updateLeadField("status", value)} options={statusOptions} />
                  <EditableField label="OEM" value={leadData.oem} editable={isEditable} onChange={(value) => updateLeadField("oem", value)} />
                  <EditableField label="OEM A/C Mgr" value={leadData.oemAcMgr} editable={isEditable} onChange={(value) => updateLeadField("oemAcMgr", value)} />
                  <EditableField label="Product" value={leadData.product} editable={isEditable} onChange={(value) => updateLeadField("product", value)} />
                  <EditableField label="Expected Closure" value={leadData.expectedClosure} editable={isEditable} onChange={(value) => updateLeadField("expectedClosure", value)} type="date" />
                  <EditableField label="DR Number" value={leadData.drNumber} editable={isEditable} onChange={(value) => updateLeadField("drNumber", value)} monospace />
                  <EditableField label="DR Expiry" value={leadData.drExpiry} editable={isEditable} onChange={(value) => updateLeadField("drExpiry", value)} type="date" />
                  <EditableField label="Remarks" value={leadData.statusRemark} editable={isEditable} onChange={(value) => updateLeadField("statusRemark", value)} className="md:col-span-2 lg:col-span-3" />
                </div>
              </div>

              {/* Point Of Contact Management Sub-grid */}
              <div>
                <div className="mb-4 border-b border-gray-100 pb-3 flex items-center justify-between">
                  <h4 className="text-sm font-semibold tracking-[0.2em] text-gray-500">Point Of Contact Assignments</h4>
                  {isEditable && (
                    <button
                      type="button"
                      onClick={addNewPocRowItem}
                      className="flex items-center gap-1 rounded border border-blue-600 px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 transition"
                    >
                      <Plus size={14} /> Add New POC
                    </button>
                  )}
                </div>

                <div className="space-y-6">
                  {pocList.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No points of contact linked to workspace matrix parameters.</p>
                  ) : (
                    pocList.map((poc, index) => (
                      <div key={poc.poc_id || index} className="p-4 border border-slate-200 rounded-lg bg-slate-50 relative space-y-3">
                        <div className="text-xs font-bold text-slate-400 absolute top-2 right-3">POC Record #{index + 1}</div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <EditableField label="POC Full Name" value={poc.poc_name} editable={isEditable} onChange={(v) => updatePocRowField(index, "poc_name", v)} />
                          <EditableField label="Email Address" value={poc.poc_email} editable={isEditable} onChange={(v) => updatePocRowField(index, "poc_email", v)} />
                          <EditableField label="Mobile Number" value={poc.poc_contact} editable={isEditable} onChange={(v) => updatePocRowField(index, "poc_contact", v)} />
                          <EditableField label="Designation/Role" value={poc.poc_designation} editable={isEditable} onChange={(v) => updatePocRowField(index, "poc_designation", v)} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </RoleCard>

          {/* Integrated Native Audit Trail Footer Widget */}
          <div className="mt-5 rounded-lg border bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
              <h3 className="font-semibold text-sm tracking-[0.1em] text-slate-700">Audit Trail Verification Logs</h3>
              <ChevronDown size={16} className="text-slate-400" />
            </div>
            <div className="grid gap-3 text-xs md:grid-cols-3 text-gray-600">
              <div className="bg-slate-50 p-3 rounded border border-slate-100">
                <span className="block font-medium text-gray-400 mb-1">Assigned Account Manager</span>
                <span className="font-semibold text-slate-800 text-sm">{leadData?.owner || "Rinku"}</span>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-100">
                <span className="block font-medium text-gray-400 mb-1">Last System Updator</span>
                <span className="font-semibold text-slate-800 text-sm">{auditMeta.updated_by_name || "Admin"}</span>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-100">
                <span className="block font-medium text-gray-400 mb-1">Last Transaction Timestamp</span>
                <span className="font-mono text-slate-800 font-semibold text-sm">{leadData?.createdAt}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Interactive Widgets Area */}
        <div className="space-y-4">
          <Card title="Activity Timeline Logs">
            <div className="mb-4">
              <textarea
                value={newActivity}
                onChange={(event) => setNewActivity(event.target.value)}
                placeholder="Log activity summary info..."
                rows={3}
                className="w-full rounded-md border border-gray-300 p-3 text-sm resize-none focus:ring-1 focus:ring-blue-500 outline-none"
              />
              <button onClick={addActivity} className="mt-2 w-full rounded-md bg-blue-600 py-2 text-sm text-white font-medium hover:bg-blue-700">
                Record Activity
              </button>
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
              {activities.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No logged history records found.</p>
              ) : (
                activities.map((item) => (
                  <div key={item.ldactvy_id} className="rounded border bg-slate-50 p-3 text-xs space-y-1">
                    <p className="font-medium text-slate-800">{item.ldactvy_activity}</p>
                    <div className="flex justify-between text-[10px] text-gray-400 font-mono">
                      <span>User: {item.updater_name || "System"}</span>
                      <span>{item.ldactvy_last_updated ? new Date(item.ldactvy_last_updated).toLocaleDateString() : "-"}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card title="Internal Workspace Notes">
            <textarea
              value={newNote}
              onChange={(event) => setNewNote(event.target.value)}
              placeholder="Add explicit notations notes..."
              className="w-full rounded-md border p-3 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <button onClick={addNote} className="mt-2 w-full rounded-md bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700">
              Save Note Entry
            </button>
            <div className="mt-3 space-y-2 max-h-[250px] overflow-y-auto pr-1">
              {notes.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">No notation records available.</p>
              ) : (
                notes.map((note) => (
                  <div key={note.ldnts_id} className="rounded-md border bg-slate-50 p-3 text-xs">
                    <div className="flex justify-between text-[10px] font-mono text-gray-400 mb-1">
                      <span>Author: {note.author_name || "System"}</span>
                      <span>{note.ldnts_last_updated ? new Date(note.ldnts_last_updated).toLocaleDateString() : "-"}</span>
                    </div>
                    <p className="text-slate-700">{note.ldnts_note}</p>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card title="Document File Management">
            <div className="rounded-md border-2 border-dashed border-slate-200 p-4 text-center text-xs text-gray-500 relative bg-slate-50 hover:bg-slate-100 transition">
              <input
                type="file"
                onChange={uploadFile}
                disabled={uploadingFile}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full disabled:cursor-not-allowed"
              />
              {uploadingFile ? "Uploading..." : "Click here or drop files to upload"}
            </div>
            <div className="mt-3 space-y-2">
              {files.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">No file attachments bound to table mapping index.</p>
              ) : (
                files.map((file) => (
                  <div key={file.ldat_id} className="flex items-center justify-between rounded-md border p-2 bg-white text-xs shadow-sm">
                    <span className="truncate max-w-[170px] font-medium text-slate-700" title={file.ldat_attachment_name}>
                      {file.ldat_attachment_name}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        title="Download"
                        disabled={downloadingId === file.ldat_id}
                        onClick={() => downloadAttachment(file.ldat_id, file.ldat_attachment_name)}
                        className="bg-transparent p-0 border-0 disabled:opacity-30 disabled:cursor-wait"
                      >
                        <Download size={14} className="cursor-pointer text-slate-400 hover:text-blue-600" />
                      </button>
                      <Trash2
                        size={14}
                        onClick={() => deleteAttachment(file.ldat_id)}
                        className="cursor-pointer text-slate-400 hover:text-red-500"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>

      <PersonPickerModal
        isOpen={presalesModalOpen}
        title="Choose Pre-Sales Person"
        options={presalesOptions}
        currentId={leadData.presalesId}
        submitting={assigningPresales}
        onClose={() => setPresalesModalOpen(false)}
        onConfirm={handleAssignPresales}
      />

      <PersonPickerModal
        isOpen={bidManagerModalOpen}
        title="Choose Bid Manager"
        options={bidManagerOptions}
        currentId={leadData.bidManagerId}
        submitting={assigningBidManager}
        onClose={() => setBidManagerModalOpen(false)}
        onConfirm={handleAssignBidManager}
      />
    </div>
  );
}

function PersonPickerModal({ isOpen, title, options = [], currentId, submitting, onClose, onConfirm }) {
  const [selectedId, setSelectedId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedId(currentId ? String(currentId) : "");
      setSearchTerm("");
    }
  }, [isOpen, currentId]);

  if (!isOpen) return null;

  const filteredOptions = options.filter((opt) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    return (
      (opt.usr_name || "").toLowerCase().includes(query) ||
      (opt.usr_email || "").toLowerCase().includes(query)
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>

      <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          {options.length > 0 && (
            <div className="relative mb-3">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                autoFocus
                className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              />
            </div>
          )}

          {options.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4 text-center">No users available for this role.</p>
          ) : filteredOptions.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4 text-center">No users match your search.</p>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {filteredOptions.map((opt) => (
                <label
                  key={opt.usr_id}
                  className={`flex cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm transition ${
                    String(selectedId) === String(opt.usr_id)
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div>
                    <div className="font-medium text-slate-700">{opt.usr_name}</div>
                    {opt.usr_email && <div className="text-xs text-slate-400">{opt.usr_email}</div>}
                  </div>
                  <input
                    type="radio"
                    name="person-picker"
                    checked={String(selectedId) === String(opt.usr_id)}
                    onChange={() => setSelectedId(String(opt.usr_id))}
                  />
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-5 py-3">
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(selectedId)}
            disabled={submitting || !selectedId}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Saving..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ title, icon, accentColor, children }) {
  return (
    <div className={`rounded-lg border border-gray-200 border-t-4 bg-white shadow-sm ${accentColor}`}>
      <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-4">
        {icon}
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function EditableField({ label, value, editable, onChange, monospace = false, className = "", type = "text", format, parse, prefix }) {
  const displayValue = format ? format(value) : value;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-xs font-semibold text-gray-400 tracking-wider">{label}</label>
      {editable ? (
        <div className="relative">
          {prefix && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
              {prefix}
            </span>
          )}
          <input
            type={type}
            value={displayValue || ""}
            onChange={(event) => onChange(parse ? parse(event.target.value) : event.target.value)}
            className={`w-full rounded-md border border-gray-300 py-1.5 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition ${prefix ? "pl-7 pr-3" : "px-3"} ${monospace ? "font-mono bg-white" : "bg-white"}`}
          />
        </div>
      ) : (
        <div className={`rounded-md border border-slate-100 bg-slate-50/60 px-3 py-1.5 text-sm font-medium text-slate-700 ${monospace ? "font-mono" : ""}`}>
          {displayValue ? `${prefix || ""}${displayValue}` : "-"}
        </div>
      )}
    </div>
  );
}

function SelectField({ label, value, editable, onChange, options = [], className = "", idKey = "code_id", labelKey = "code_short_description" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-xs font-semibold text-gray-400 tracking-wider">{label}</label>
      {editable ? (
        <select
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
        >
          <option value="">Select {label}</option>
          {!options.some((opt) => String(opt[idKey]) === String(value) || opt[labelKey] === value) && value && (
            <option value={value}>{value}</option>
          )}
          {options.map((opt) => (
            <option key={opt[idKey]} value={idKey === "code_id" ? opt[labelKey] : opt[idKey]}>
              {opt[labelKey]}
            </option>
          ))}
        </select>
      ) : (
        <div className="rounded-md border border-slate-100 bg-slate-50/60 px-3 py-1.5 text-sm font-medium text-slate-700">
          {value || "-"}
        </div>
      )}
    </div>
  );
}

function Card({ title, action, children }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      {title && (
        <div className="mb-3 flex items-center justify-between border-b border-slate-50 pb-2">
          <h3 className="font-semibold text-sm text-slate-700">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}